import { formatOptions, getTimeOption, getUserName, setStatusValue } from "@/react/shared/helper";
import {
   MultiSelectFilterRenderer,
   type CellValueChangeParams,
   type DataTableConfig,
} from "@procore/data-table";
import { setJobValue } from "../people-list/helpers";
import { DateUtils } from "@/lib/utils/date";
import { CustomFieldType } from "@laborchart-modules/common";
import {
   ResourceType,
   type ColumnMap,
   type CustomFieldProjectType,
   type SortModel,
} from "@/react/prop-types";
import { FilterFieldType } from "@laborchart-modules/common/dist/rethink/schemas/generated-reports/enums/common";
import type { ListServerFilter } from "../data-table/data-table-utils";
import { convertDataTableFilters, sortOrderMap } from "../data-table/data-table-utils";
import type { CoreApiSort } from "./request-list-prop-types";
import type { FindRequestsSortBy } from "@laborchart-modules/common/dist/reql-builder/procedures/find-requests";
import { RequestsColumnKey } from "@laborchart-modules/common/dist/rethink/schemas/column-headers/requests-columns";
import type { NotifiableError } from "@bugsnag/js";
import { BUGSNAG_META_TAB } from "@/lib/utils/bugsnag-content-helper";
import {
   convertDetachedDayToUtcDateWithoutTimezoneOffset,
   getDetachedDay,
} from "@laborchart-modules/common/dist/datetime";
import type { AuthType, UpdateRequestsPayload } from "@laborchart-modules/lc-core-api";
import { RequestStore } from "@/stores/request-store.core";
import type { GetAssignmentCreationSupportDataResponse } from "@laborchart-modules/lc-core-api/dist/api/legacy/get-assignment-creation-support-data";
import type { SerializedFindCombinedRequest } from "@laborchart-modules/lc-core-api/dist/api/requests/find-combined-requests";
import { getIdAndLabel } from "../assignment-list/assignment-list-helpers";
import { reportError } from "@/utils/logging-utils";
import { StatusFilter } from "../data-table/custom-filters/status-filter";
import { DateFilter } from "../data-table/custom-filters/date-filter";
import MultiSelectPillFilter from "../data-table/custom-filters/multiselect-pill-filter";
import { EquipmentRequestStore } from "@/stores/equipment-requests-store.core";
import type { ColorSelectValue } from "../data-table/ColorSelectComponent/ColorSelectColumn";
import type { SerializedCachedEquipmentCompanyType } from "@laborchart-modules/common/dist/postgres/serializers/cached-equipment-company-type-serializer";
import type { CustomField } from "@/react/prop-types";

const DATE_FIELDS: string[] = ["start_day", "end_day"];
const NUMERIC_FIELDS: string[] = ["percent_allocated"];
const NESTED_FIELDS_TYPE: FilterFieldType[] = [FilterFieldType.BOOL, FilterFieldType.MULTI_SELECT];
const NUMERIC_FIELDS_TYPE: FilterFieldType[] = [FilterFieldType.NUMBER, FilterFieldType.CURRENCY];

export const defaultRequestListTableConfig: DataTableConfig = {
   columnState: [
      {
         field: "project",
         hidden: false,
         pinned: null,
         width: 280,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "job_number",
         hidden: false,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "job_title",
         hidden: false,
         pinned: null,
         width: 180,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "tag_instances",
         hidden: false,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "project_status",
         hidden: true,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "request_status",
         hidden: false,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "start_day",
         hidden: false,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "end_day",
         hidden: false,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "start_time",
         hidden: false,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "end_time",
         hidden: false,
         pinned: null,
         width: 150,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "cost_code_name", //category
         hidden: false,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "label_name", // subcategory
         hidden: false,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "percent_allocated",
         hidden: true,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "created_by",
         hidden: false,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
      {
         field: "created_at",
         hidden: false,
         pinned: null,
         width: 200,
         sort: null,
         sortIndex: null,
         rowGroup: false,
         rowGroupIndex: null,
      },
   ],
   columnGroupState: [],
   filters: {},
   rowHeight: 49,
   serverFilters: [],
};

export const getJobTitle = (request: SerializedFindCombinedRequest) => {
   if (request.request_type === ResourceType.EQUIPMENT) {
      return request.equipment_type
         ? {
              label: request.equipment_type.name,
              color: "",
              shape: "circle",
              id: request.equipment_type.id,
           }
         : null;
   }
   // falls back to labor
   return request.job_title ? setJobValue(request.job_title) : null;
};

export function mapApiRequestToTableRequest(
   requestData: SerializedFindCombinedRequest[],
   isLastNameFirst: boolean,
   assignmentCreationSupportData: GetAssignmentCreationSupportDataResponse | null,
) {
   return requestData.map((requestList) => {
      const categoryOptions = assignmentCreationSupportData?.data
         ? formatOptions(
              assignmentCreationSupportData?.data.grouped_cost_code_options[requestList.project.id],
           )
         : [];
      const subCategoryOptions = assignmentCreationSupportData?.data
         ? assignmentCreationSupportData?.data.grouped_label_options
         : [];
      const requestDetails: Record<string, any> = {
         ...requestList,
         project: requestList.project.name,
         job_number: requestList.project.job_number,
         start_day: convertDetachedDayToUtcDateWithoutTimezoneOffset(requestList.start_day),
         end_day: convertDetachedDayToUtcDateWithoutTimezoneOffset(requestList.end_day),
         start_time: getTimeOption(requestList.start_time),
         end_time: getTimeOption(requestList.end_time),
         request_status: requestList.status ? setStatusValue(requestList.status) : null,
         created_by: requestList.creator
            ? getUserName(requestList.creator.name, isLastNameFirst)
            : null,
         created_at: requestList.created_at ? new Date(requestList.created_at) : null,
         cost_code_name: getIdAndLabel(requestList.category), // category
         label_name: getIdAndLabel(requestList.subcategory), // subcategory
         job_title: getJobTitle(requestList),
         tag_instances: requestList.tags?.map((tag) => ({
            label: tag.abbreviation,
            color: tag.color,
            value: tag.id,
            name: tag.name,
            shape: "pill",
            componentName: "multiselect",
         })),
         work_days: DateUtils.getFormattedWorkDays(requestList.work_days),
         work_days_object: requestList.work_days,
         categoryOptions: categoryOptions,
         subCategoryOptions: subCategoryOptions,
      };

      // Adding custom fields as individual attributes
      if (requestList.custom_fields) {
         requestList.custom_fields.forEach((customField: CustomFieldProjectType) => {
            const { integration_name, value, type } = customField;
            if (type === CustomFieldType.DATE && value) {
               requestDetails[integration_name] = DateUtils.getAttachedDate(value);
            } else if (type === CustomFieldType.HEX_COLOR && value) {
               requestDetails[integration_name] = {
                  label: "",
                  color: value,
                  shape: "square",
               };
            } else {
               requestDetails[integration_name] = value;
            }
         });
      }

      return requestDetails;
   });
}

export const filterNameMaps: { [key: string]: string } = {
   project: "Project",
   job_title: "People Job Titles",
   project_status: "Project Status",
   request_status: "Request Status",
   start_day: "Starting",
   end_day: "Ending",
   created_by: "Created By",
   tag_instances: "Tags",
};

const filterPropertyMaps: { [key: string]: string } = {
   project: "project_id",
   job_title: "position_id",
   request_status: "status_id",
   created_by: "creator_id",
   tag_instances: "tag_ids",
};

const filterTypeMaps: { [key: string]: FilterFieldType } = {
   project: FilterFieldType.SELECT,
   job_title: FilterFieldType.SELECT,
   project_status: FilterFieldType.SELECT,
   request_status: FilterFieldType.SELECT,
   start_day: FilterFieldType.DATE,
   end_day: FilterFieldType.DATE,
   created_by: FilterFieldType.SELECT,
   tag_instances: FilterFieldType.MULTI_SELECT,
   bool: FilterFieldType.BOOL,
   number: FilterFieldType.NUMBER,
   text: FilterFieldType.TEXT,
   "multi-select": FilterFieldType.MULTI_SELECT,
   date: FilterFieldType.DATE,
   select: FilterFieldType.SELECT,
   currency: FilterFieldType.CURRENCY,
};

export type FetchAllRequestsParams = {
   configKey: string;
   groupId: string;
   search: string | undefined;
   isLastNameFirst: boolean;
   assignmentCreationSupportData: GetAssignmentCreationSupportDataResponse | null;
};

/**
 * Fetches all labor requests matching current filters for "select all" bulk actions.
 * Used to reduce cognitive complexity in the data table component.
 */
export async function fetchAllRequests({
   configKey,
   groupId,
   search,
   isLastNameFirst,
   assignmentCreationSupportData,
}: FetchAllRequestsParams) {
   const filters = JSON.parse(localStorage.getItem(configKey) ?? "{}");
   const coreApiFilters = filters.serverFilters
      ? convertRequestDataTableFilters(filters.serverFilters)
      : [];
   const data: SerializedFindCombinedRequest[] = [];
   const stream = await RequestStore.findCombinedRequestsStream({
      filters: coreApiFilters,
      group_id: groupId === "my-groups" ? undefined : groupId,
      search: search ?? undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
   }).stream;
   for await (const request of stream) {
      if (request.request_type === ResourceType.LABOR) {
         data.push(request);
      }
   }
   return mapApiRequestToTableRequest(data, isLastNameFirst, assignmentCreationSupportData);
}

export const convertRequestDataTableFilters = (filters: ListServerFilter[]) =>
   convertDataTableFilters(
      filters,
      {
         nameMapper: filterNameMaps,
         typeMapper: filterTypeMaps,
         propertyMapper: filterPropertyMaps,
      },
      {
         date: DATE_FIELDS,
         numeric: NUMERIC_FIELDS,
         numericTypes: NUMERIC_FIELDS_TYPE,
         nestedTypes: NESTED_FIELDS_TYPE,
      },
   );

export enum FieldMapping {
   JOB_TITLE = "job_title_id",
   REQUEST_STATUS = "status_id",
   START_DAY = "start_day",
   END_DAY = "end_day",
   START_TIME = "start_time",
   END_TIME = "end_time",
   COST_CODE_NAME = "category_id",
   LABEL_NAME = "subcategory_id",
   PERCENT_ALLOCATED = "percent_allocated",
   EQUIPMENT_TYPE_ID = "type_id",
}

type inlineRequestUpdatePayload = UpdateRequestsPayload<AuthType.SESSION> & {
   [FieldMapping.EQUIPMENT_TYPE_ID]?: string;
   [customFieldId: string]: unknown;
};

const buildInlineRequestUpdatePayload = (
   id: string,
   fieldKey: string,
   newValue: any,
   isEquipmentRequest: boolean,
   updatePayload: inlineRequestUpdatePayload,
) => {
   switch (fieldKey) {
      case "JOB_TITLE":
         if (isEquipmentRequest) {
            updatePayload[FieldMapping.EQUIPMENT_TYPE_ID] = newValue.id;
         } else {
            updatePayload[FieldMapping.JOB_TITLE] = newValue.id;
         }
         break;
      case "REQUEST_STATUS":
         {
            const statusId = newValue?.id;
            updatePayload[FieldMapping.REQUEST_STATUS] = statusId === "" ? null : statusId ?? null;
         }
         break;
      case "START_DAY":
         updatePayload[FieldMapping.START_DAY] = getDetachedDay(newValue);
         break;
      case "END_DAY":
         updatePayload[FieldMapping.END_DAY] = getDetachedDay(newValue);
         break;
      case "START_TIME":
         updatePayload[FieldMapping.START_TIME] = newValue?.value ?? null;
         break;
      case "END_TIME":
         updatePayload[FieldMapping.END_TIME] = newValue?.value ?? null;
         break;
      case "COST_CODE_NAME":
         if (newValue == null) {
            updatePayload[FieldMapping.COST_CODE_NAME] = null;
            updatePayload[FieldMapping.LABEL_NAME] = null;
         } else {
            updatePayload[FieldMapping.COST_CODE_NAME] = newValue.id;
         }
         break;
      case "LABEL_NAME":
         updatePayload[FieldMapping.LABEL_NAME] = newValue?.id ?? null;
         break;
      case "PERCENT_ALLOCATED":
         updatePayload[FieldMapping.PERCENT_ALLOCATED] = Number(newValue);
         break;
   }
};

export const updateInLineRequest = async (
   params: CellValueChangeParams<any>,
   customFields: CustomField[],
) => {
   const { id, request_type } = params.rowData;
   const { field: fieldName, newValue } = params;
   const isEquipmentRequest = request_type === ResourceType.EQUIPMENT;
   const customField = customFields.find(
      (customField) => customField.integration_name === fieldName,
   );
   const fieldKey = fieldName.toUpperCase();
   const updatePayload: inlineRequestUpdatePayload = { id };

   try {
      if (fieldKey in FieldMapping) {
         buildInlineRequestUpdatePayload(id, fieldKey, newValue, isEquipmentRequest, updatePayload);
      } else if (customField) {
         let value = newValue;
         if (customField.type === CustomFieldType.HEX_COLOR) {
            value = newValue.color;
         } else if (customField.type === CustomFieldType.DATE) {
            value = getDetachedDay(newValue);
         } else if (
            customField.type === CustomFieldType.NUMBER ||
            customField.type === CustomFieldType.CURRENCY
         ) {
            value = Number(newValue);
         }
         updatePayload.custom_fields = { [customField.id]: value };
      }
      isEquipmentRequest
         ? EquipmentRequestStore.updateEquipmentRequest(id, updatePayload)
         : RequestStore.updateRequestsStream([updatePayload]);
   } catch (error: any) {
      reportError(error as NotifiableError, "request-list__updateInLineRequest", {
         [BUGSNAG_META_TAB.REQUEST]: {
            id,
            field: fieldName,
            field_key: fieldKey,
            new_value: newValue,
            update_payload: updatePayload,
            validation_message: error?.validation?.[0]?.message,
         },
      });
   }
};
// Sort Field Maps
export const sortFieldMaps: { [key: string]: FindRequestsSortBy } = {
   project: RequestsColumnKey.PROJECT_NAME,
   job_number: RequestsColumnKey.JOB_NUMBER,
   job_title: RequestsColumnKey.POSITION_NAME,
   start_day: RequestsColumnKey.START_DAY,
   end_day: RequestsColumnKey.END_DAY,
   start_time: RequestsColumnKey.START_TIME,
   end_time: RequestsColumnKey.END_TIME,
   created_by: RequestsColumnKey.CREATOR_NAME,
   created_at: RequestsColumnKey.CREATED_AT,
};

export function convertDataTableSort(sort: SortModel[]): CoreApiSort {
   if (sort.length == 0) return {};

   const sortObj: CoreApiSort = {
      sort_by: sortFieldMaps[sort[0].field],
      direction: sortOrderMap[sort[0].sort],
   };

   return sortObj;
}

export const fieldTranslationKeyMap: ColumnMap = {
   project: "views.company.workforce_planning.project",
   job_number: "views.company.workforce_planning.projects.project_number",
   job_title: "views.company.workforce_planning.people.job_title",
   tag_instances: "views.company.workforce_planning.tags",
   request_status: "views.company.workforce_planning.request.status",
   start_day: "views.company.workforce_planning.request.start_date",
   end_day: "views.company.workforce_planning.request.end_date",
   start_time: "views.company.workforce_planning.request.start_time",
   end_time: "views.company.workforce_planning.request.end_time",
   cost_code_name: "views.company.workforce_planning.category",
   label_name: "views.company.workforce_planning.subcategory",
   percent_allocated: "views.company.workforce_planning.assignment.percent_allocated",
   created_by: "views.company.workforce_planning.request.created_by",
   created_at: "views.company.workforce_planning.request.created",
};

export const legacyFieldNamesMap: ColumnMap = {
   project: "project_name",
   job_number: "job_number",
   job_title: "position_name",
   tag_instances: "tags",
   request_status: "status",
   start_day: "start_day",
   end_day: "end_day",
   start_time: "start_time",
   end_time: "end_time",
   cost_code_name: "cost_code_name",
   label_name: "label_name",
   percent_allocated: "percent_allocated",
   created_by: "creator_name",
   created_at: "created_at",
};

export const columnHeadersMap: ColumnMap = {
   project_name: "project",
   job_number: "job_number",
   position_name: "job_title",
   tags: "tag_instances",
   status: "request_status",
   start_day: "start_day",
   end_day: "end_day",
   creator_name: "created_by",
   cost_code_name: "cost_code_name",
   label_name: "label_name",
   created_at: "created_at",
   start_time: "start_time",
   end_time: "end_time",
   percent_allocated: "percent_allocated",
   custom_fields: "custom_fields",
};

export const filterFieldMap: ColumnMap = {
   position_id: "job_title",
   project_id: "project",
   project_status: "project_status",
   status_id: "request_status",
   start_day: "start_day",
   end_day: "end_day",
   tag_ids: "tag_instances",
   creator_id: "created_by",
};

export const filterRendererMap: ColumnMap = {
   position_id: MultiSelectFilterRenderer,
   project_id: MultiSelectFilterRenderer,
   project_status: StatusFilter,
   status_id: MultiSelectFilterRenderer,
   start_day: DateFilter,
   end_day: DateFilter,
   tag_ids: MultiSelectPillFilter,
   creator_id: MultiSelectFilterRenderer,
};

export function mapEquipmentTypesToColorSelectOptions(
   equipmentTypes: SerializedCachedEquipmentCompanyType[] | undefined,
): ColorSelectValue[] {
   if (!equipmentTypes?.length) {
      return [];
   }
   return equipmentTypes.map((et) => {
      return {
         id: et.id,
         label: et.name,
         color: "",
         shape: "circle",
      };
   }) as ColorSelectValue[];
}
